package com.partejar.app.core.ui.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarTheme

enum class PartejarAlertTone {
    INFO,
    ATTENTION,
    ERROR,
    SUCCESS
}

@Composable
fun PartejarAlert(
    title: String,
    tone: PartejarAlertTone,
    modifier: Modifier = Modifier,
    action: @Composable RowScope.() -> Unit = {},
    content: @Composable ColumnScope.() -> Unit
) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions
    val colors = alertColors(tone)

    Surface(
        modifier = modifier.fillMaxWidth(),
        shape = MaterialTheme.shapes.medium,
        color = colors.container,
        contentColor = colors.content,
        border = BorderStroke(dimensions.strokeThin, colors.accent)
    ) {
        Row(
            modifier = Modifier.padding(spacing.space16),
            horizontalArrangement = Arrangement.spacedBy(spacing.space12),
            verticalAlignment = Alignment.Top
        ) {
            Icon(
                imageVector = alertIcon(tone),
                contentDescription = null,
                modifier = Modifier.size(dimensions.iconDefault),
                tint = colors.accent
            )
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(spacing.space4)
            ) {
                Text(
                    text = title,
                    style = MaterialTheme.typography.titleSmall
                )
                content()
            }
            Row(content = action)
        }
    }
}

private data class AlertColors(
    val container: Color,
    val content: Color,
    val accent: Color
)

@Composable
private fun alertColors(tone: PartejarAlertTone): AlertColors = when (tone) {
    PartejarAlertTone.INFO -> AlertColors(
        PartejarTheme.colors.infoContainer,
        PartejarTheme.colors.onInfoContainer,
        PartejarTheme.colors.info
    )
    PartejarAlertTone.ATTENTION -> AlertColors(
        PartejarTheme.colors.attentionContainer,
        PartejarTheme.colors.onAttentionContainer,
        PartejarTheme.colors.attention
    )
    PartejarAlertTone.ERROR -> AlertColors(
        MaterialTheme.colorScheme.errorContainer,
        MaterialTheme.colorScheme.onErrorContainer,
        MaterialTheme.colorScheme.error
    )
    PartejarAlertTone.SUCCESS -> AlertColors(
        PartejarTheme.colors.successContainer,
        PartejarTheme.colors.onSuccessContainer,
        PartejarTheme.colors.success
    )
}

private fun alertIcon(tone: PartejarAlertTone): ImageVector = when (tone) {
    PartejarAlertTone.INFO -> PartejarIcons.Feedback.info
    PartejarAlertTone.ATTENTION -> PartejarIcons.Feedback.attention
    PartejarAlertTone.ERROR -> PartejarIcons.Feedback.error
    PartejarAlertTone.SUCCESS -> PartejarIcons.Feedback.success
}
